import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { LandingLayout } from '@/components/landing/LandingLayout';
import { PainAwareLandingHero } from '@/components/landing/PainAwareLandingHero';
import { ValuePropsSection } from '@/components/landing/ValuePropsSection';
import { BottomCtaBlock } from '@/components/landing/BottomCtaBlock';
import { AiSmartAutomationSection } from '@/components/landing/AiSmartAutomationSection';
import { FaqSection } from '@/components/landing/FaqSection';
import { BeforeAfterScenario } from '@/components/landing/BeforeAfterScenario';
import { StickyFloatingCTA } from '@/components/landing/StickyFloatingCTA';
import { TrustBadges } from '@/components/landing/TrustBadges';
import { StatsSection } from '@/components/landing/StatsSection';
import { TestimonialCarousel } from '@/components/landing/TestimonialCarousel';
import { RoiSection } from '@/components/landing/RoiSection';
import { ComparisonTable } from '@/components/landing/ComparisonTable';
import { FinalCTASection } from '@/components/landing/FinalCTASection';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Download, ArrowRight, Bed, Bath, Sparkles, Clock, XCircle, Camera, Bell, CheckSquare } from 'lucide-react';
import { AiAssistantSummary } from '@/components/landing/AiAssistantSummary';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const AirbnbTurnoverChecklist = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const ChecklistPreviewSection = () => {
    const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
    
    return (
      <section ref={ref} className="py-16 container mx-auto px-4 max-w-4xl">
        <h2 className="text-3xl font-bold mb-8 text-center">{t('turnoverChecklist.checklistPreview.title')}</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {[
            {
              icon: Clock,
              title: t('turnoverChecklist.checklistPreview.section1Title'),
              badge: t('turnoverChecklist.checklistPreview.section1Badge'),
              items: [
                t('turnoverChecklist.checklistPreview.section1Item1'),
                t('turnoverChecklist.checklistPreview.section1Item2'),
                t('turnoverChecklist.checklistPreview.section1Item3')
              ]
            },
            {
              icon: Sparkles,
              title: t('turnoverChecklist.checklistPreview.section2Title'),
              badge: t('turnoverChecklist.checklistPreview.section2Badge'),
              items: [
                t('turnoverChecklist.checklistPreview.section2Item1'),
                t('turnoverChecklist.checklistPreview.section2Item2'),
                t('turnoverChecklist.checklistPreview.section2Item3'),
                t('turnoverChecklist.checklistPreview.section2Item4')
              ]
            },
            {
              icon: Camera,
              title: t('turnoverChecklist.checklistPreview.section3Title'),
              badge: null,
              items: [
                t('turnoverChecklist.checklistPreview.section3Item1'),
                t('turnoverChecklist.checklistPreview.section3Item2'),
                t('turnoverChecklist.checklistPreview.section3Item3'),
                t('turnoverChecklist.checklistPreview.section3Item4')
              ]
            },
            {
              icon: CheckSquare,
              title: t('turnoverChecklist.checklistPreview.section4Title'),
              badge: null,
              items: [
                t('turnoverChecklist.checklistPreview.section4Item1'),
                t('turnoverChecklist.checklistPreview.section4Item2'),
                t('turnoverChecklist.checklistPreview.section4Item3')
              ]
            }
          ].map((section, index) => (
            <Card 
              key={index} 
              className={`group hover:shadow-2xl hover:-translate-y-2 hover:scale-[1.02] transition-all duration-500 ease-out ${
                isVisible ? 'opacity-100 translate-y-0 rotate-0' : 'opacity-0 translate-y-20 rotate-3'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <section.icon className="w-5 h-5 text-primary transition-all duration-300 group-hover:scale-125 group-hover:rotate-12 animate-[fade-in_0.5s_ease-out]" />
                  {section.title}
                  {section.badge && (
                    <Badge variant="destructive" className="ml-auto animate-pulse hover:scale-110 transition-transform">{section.badge}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  {section.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    );
  };

  const SeeRealExamplesSection = () => {
    return (
      <section className="py-16 bg-gradient-to-b from-background to-secondary/10">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-4 text-center">{t('turnoverChecklist.seeHowItWorks.title')}</h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            {t('turnoverChecklist.seeHowItWorks.subtitle')}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Card className="group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
              <CardContent className="pt-6 overflow-hidden">
                <div className="overflow-hidden rounded-lg mb-4">
                  <img 
                    src="/screenshots/portfolio.webp" 
                    alt="Calendar view with scheduled turnovers"
                    className="rounded-lg border border-border group-hover:scale-110 transition-transform duration-500 ease-out"
                  />
                </div>
                <h3 className="font-semibold mb-2">{t('turnoverChecklist.seeHowItWorks.item1Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('turnoverChecklist.seeHowItWorks.item1Description')}
                </p>
              </CardContent>
            </Card>
            
            <Card className="group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
              <CardContent className="pt-6 overflow-hidden">
                <div className="overflow-hidden rounded-lg mb-4">
                  <img 
                    src="/screenshots/income.webp" 
                    alt="Mobile view of cleaner checklist"
                    className="rounded-lg border border-border group-hover:scale-110 transition-transform duration-500 ease-out"
                  />
                </div>
                <h3 className="font-semibold mb-2">{t('turnoverChecklist.seeHowItWorks.item2Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('turnoverChecklist.seeHowItWorks.item2Description')}
                </p>
              </CardContent>
            </Card>
            
            <Card className="group hover:shadow-2xl hover:-translate-y-2 transition-all duration-500">
              <CardContent className="pt-6 overflow-hidden">
                <div className="overflow-hidden rounded-lg mb-4">
                  <img 
                    src="/screenshots/expenses.webp" 
                    alt="Photo proof upload interface"
                    className="rounded-lg border border-border group-hover:scale-110 transition-transform duration-500 ease-out"
                  />
                </div>
                <h3 className="font-semibold mb-2">{t('turnoverChecklist.seeHowItWorks.item3Title')}</h3>
                <p className="text-sm text-muted-foreground">
                  {t('turnoverChecklist.seeHowItWorks.item3Description')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  };

  const DownloadSection = () => {
    return (
      <section className="py-16 bg-gradient-to-r from-primary/10 via-secondary/5 to-primary/10">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-4">{t('turnoverChecklist.download.title')}</h2>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>{t('turnoverChecklist.download.feature1')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>{t('turnoverChecklist.download.feature2')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>{t('turnoverChecklist.download.feature3')}</span>
                </div>
              </div>
              <Button size="lg" className="text-lg px-8 py-6 mb-4 hover-scale group active:scale-95 transition-all duration-300" asChild>
                <a href="/downloads/airbnb-turnover-checklist.pdf" download>
                  <Download className="mr-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  {t('turnoverChecklist.download.ctaText')}
                </a>
              </Button>
              <p className="text-sm text-muted-foreground">
                {t('turnoverChecklist.download.stats')}
              </p>
            </div>
            
            <div className="relative">
              <div className="bg-white p-4 rounded-lg shadow-2xl border-2 border-primary/20">
                <div className="aspect-[8.5/11] bg-muted rounded flex items-center justify-center">
                  <div className="text-center p-8">
                    <Download className="w-16 h-16 text-primary mx-auto mb-4" />
                    <p className="font-semibold text-lg">{t('turnoverChecklist.download.previewTitle')}</p>
                    <p className="text-sm text-muted-foreground">{t('turnoverChecklist.download.previewSubtitle')}</p>
                  </div>
                </div>
              </div>
              <Badge className="absolute -top-4 -right-4 animate-pulse">{t('turnoverChecklist.download.badge')}</Badge>
            </div>
          </div>
        </div>
      </section>
    );
  };

  const RealWorldScenarioSection = () => {
    return (
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-8 text-center">
            {t('turnoverChecklist.realWorld.title')}
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-destructive/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-destructive" />
                  {t('turnoverChecklist.realWorld.manualTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { time: t('turnoverChecklist.realWorld.manualStep1Time'), text: t('turnoverChecklist.realWorld.manualStep1') },
                    { time: t('turnoverChecklist.realWorld.manualStep2Time'), text: t('turnoverChecklist.realWorld.manualStep2') },
                    { time: t('turnoverChecklist.realWorld.manualStep3Time'), text: t('turnoverChecklist.realWorld.manualStep3') },
                    { time: t('turnoverChecklist.realWorld.manualStep4Time'), text: t('turnoverChecklist.realWorld.manualStep4') },
                    { time: t('turnoverChecklist.realWorld.manualStep5Time'), text: t('turnoverChecklist.realWorld.manualStep5') },
                    { time: t('turnoverChecklist.realWorld.manualStep6Time'), text: t('turnoverChecklist.realWorld.manualStep6') }
                  ].map((step, i) => (
                  <div key={i} className="flex gap-4 items-start">
                      <Badge variant="outline" className="shrink-0 hover:animate-pulse transition-all hover:scale-110">{step.time}</Badge>
                      <p className="text-sm">{step.text}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-destructive/10 rounded-lg">
                  <p className="text-sm font-semibold text-destructive">
                    {t('turnoverChecklist.realWorld.manualTotal')}
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-primary/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  {t('turnoverChecklist.realWorld.autoTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { time: t('turnoverChecklist.realWorld.autoStep1Time'), text: t('turnoverChecklist.realWorld.autoStep1') },
                    { time: t('turnoverChecklist.realWorld.autoStep2Time'), text: t('turnoverChecklist.realWorld.autoStep2') },
                    { time: t('turnoverChecklist.realWorld.autoStep3Time'), text: t('turnoverChecklist.realWorld.autoStep3') },
                    { time: t('turnoverChecklist.realWorld.autoStep4Time'), text: t('turnoverChecklist.realWorld.autoStep4') },
                    { time: t('turnoverChecklist.realWorld.autoStep5Time'), text: t('turnoverChecklist.realWorld.autoStep5') },
                    { time: t('turnoverChecklist.realWorld.autoStep6Time'), text: t('turnoverChecklist.realWorld.autoStep6') }
                  ].map((step, i) => (
                    <div key={i} className="flex gap-4 items-start">
                      <Badge variant="outline" className="shrink-0">{step.time}</Badge>
                      <p className="text-sm">{step.text}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-primary/10 rounded-lg">
                  <p className="text-sm font-semibold text-primary">
                    {t('turnoverChecklist.realWorld.autoTotal')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    );
  };

  const CommonMistakesSection = () => {
    const { ref, isVisible } = useScrollReveal<HTMLDivElement>();
    
    return (
      <section ref={ref} className="py-16 bg-gradient-to-b from-background to-secondary/5">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-4 text-center">{t('turnoverChecklist.mistakes.title')}</h2>
          <p className="text-center text-muted-foreground mb-12">
            {t('turnoverChecklist.mistakes.subtitle')}
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                mistake: t('turnoverChecklist.mistakes.mistake1'),
                solution: t('turnoverChecklist.mistakes.solution1'),
                example: t('turnoverChecklist.mistakes.example1')
              },
              {
                mistake: t('turnoverChecklist.mistakes.mistake2'),
                solution: t('turnoverChecklist.mistakes.solution2'),
                example: t('turnoverChecklist.mistakes.example2')
              },
              {
                mistake: t('turnoverChecklist.mistakes.mistake3'),
                solution: t('turnoverChecklist.mistakes.solution3'),
                example: t('turnoverChecklist.mistakes.example3')
              },
              {
                mistake: t('turnoverChecklist.mistakes.mistake4'),
                solution: t('turnoverChecklist.mistakes.solution4'),
                example: t('turnoverChecklist.mistakes.example4')
              }
            ].map((item, index) => (
              <Card 
                key={index}
                className={`group hover:shadow-2xl hover:-translate-y-2 hover:animate-[wiggle_0.5s_ease-in-out] transition-all duration-500 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <CardTitle className="flex items-start gap-2 text-lg">
                    <XCircle className="w-5 h-5 text-destructive mt-1 flex-shrink-0 drop-shadow-[0_0_8px_hsl(var(--destructive)/0.5)] group-hover:scale-125 group-hover:rotate-12 transition-all duration-300" />
                    <div>
                      <p className="text-destructive mb-2">{t('turnoverChecklist.mistakes.mistakeLabel')}</p>
                      <p className="font-normal text-base">{item.mistake}</p>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-primary mb-1">{t('turnoverChecklist.mistakes.solutionLabel')}</p>
                    <p className="text-sm">{item.solution}</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground italic">
                      "{item.example}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('turnoverChecklist.seo.title')}</title>
        <meta name="description" content={t('turnoverChecklist.seo.description')} />
        <link rel="canonical" href={`https://rental.shift-box.com/${i18n.language}/airbnb-turnover-checklist-free-download`} />
        
        <link rel="alternate" hrefLang="en" href="https://rental.shift-box.com/en/airbnb-turnover-checklist-free-download" />
        <link rel="alternate" hrefLang="es" href="https://rental.shift-box.com/es/airbnb-turnover-checklist-free-download" />
        <link rel="alternate" hrefLang="de" href="https://rental.shift-box.com/de/airbnb-turnover-checklist-free-download" />
        <link rel="alternate" hrefLang="fr" href="https://rental.shift-box.com/fr/airbnb-turnover-checklist-free-download" />
        <link rel="alternate" hrefLang="it" href="https://rental.shift-box.com/it/airbnb-turnover-checklist-free-download" />
        <link rel="alternate" hrefLang="x-default" href="https://rental.shift-box.com/en/airbnb-turnover-checklist-free-download" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "SoftwareApplication",
                "name": "Rental Manager",
                "applicationCategory": "BusinessApplication",
                "offers": {
                  "@type": "Offer",
                  "price": "12",
                  "priceCurrency": "USD"
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "ratingCount": "847"
                }
              },
              {
                "@type": "HowTo",
                "name": "How to use the Airbnb turnover checklist",
                "step": [
                  {
                    "@type": "HowToStep",
                    "name": "Before checkout",
                    "text": "Send checkout instructions, verify payout, schedule cleaner"
                  },
                  {
                    "@type": "HowToStep",
                    "name": "During cleaning",
                    "text": "Complete kitchen, bathroom, bedroom, and common area tasks"
                  },
                  {
                    "@type": "HowToStep",
                    "name": "After cleaning",
                    "text": "Take photos, note damages, restock essentials, test amenities"
                  }
                ]
              },
              {
                "@type": "FAQPage",
                "mainEntity": ((t('turnoverChecklist.seo.faq', { returnObjects: true }) || []) as Array<{ question: string; answer: string }>).map((faq) => ({
                  "@type": "Question",
                  "name": faq.question,
                  "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                  }
                }))
              }
            ]
          })}
        </script>
      </Helmet>

      <LandingLayout>
        {/* Sticky Floating CTA */}
        <StickyFloatingCTA />

        {/* Hero */}
        <div className="animate-fade-in">
          <PainAwareLandingHero
            h1={t('turnoverChecklist.hero.h1')}
            subtitle={t('turnoverChecklist.hero.subtitle')}
            ctaText={t('turnoverChecklist.hero.ctaText')}
            ctaAction={() => navigate('/auth')}
            visualContent={
              <div className="relative">
                <Badge className="absolute -top-4 left-1/2 -translate-x-1/2 z-10 animate-[fade-in_0.5s_ease-out_0.1s_both] hover:scale-110 transition-transform shadow-lg">
                  {t('turnoverChecklist.hero.badge')}
                </Badge>
                <div className="overflow-hidden rounded-lg">
                  <img 
                    src="/screenshots/exports.webp" 
                    alt="Airbnb turnover checklist PDF preview"
                    className="rounded-lg shadow-2xl border border-border mt-4 hover:scale-110 transition-transform duration-500 ease-out animate-[slide-in-right_0.7s_ease-out_0.3s_both]"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg shadow-lg animate-[scale-in_0.5s_ease-out_0.5s_both]">
                  <p className="text-sm font-semibold">{t('turnoverChecklist.hero.visualBadge')}</p>
                </div>
              </div>
            }
          />
        </div>

        {/* Trust Badges */}
        <TrustBadges />

        {/* Stats Section */}
        <StatsSection />

        {/* Unique focus paragraph */}
        <section className="py-8 container mx-auto px-4 max-w-3xl">
          <p className="text-lg text-center text-muted-foreground" dangerouslySetInnerHTML={{ __html: t('turnoverChecklist.focus.text') }} />
        </section>

        {/* Checklist preview - Enhanced */}
        <ChecklistPreviewSection />

        {/* Testimonials */}
        <TestimonialCarousel testimonials={[
          {
            name: t('turnoverChecklist.testimonials.testimonial1Name'),
            role: t('turnoverChecklist.testimonials.testimonial1Role'),
            content: t('turnoverChecklist.testimonials.testimonial1Content'),
            rating: 5
          },
          {
            name: t('turnoverChecklist.testimonials.testimonial2Name'),
            role: t('turnoverChecklist.testimonials.testimonial2Role'),
            content: t('turnoverChecklist.testimonials.testimonial2Content'),
            rating: 5
          },
          {
            name: t('turnoverChecklist.testimonials.testimonial3Name'),
            role: t('turnoverChecklist.testimonials.testimonial3Role'),
            content: t('turnoverChecklist.testimonials.testimonial3Content'),
            rating: 5
          },
          {
            name: t('turnoverChecklist.testimonials.testimonial4Name'),
            role: t('turnoverChecklist.testimonials.testimonial4Role'),
            content: t('turnoverChecklist.testimonials.testimonial4Content'),
            rating: 5
          }
        ]} />

        {/* ROI Section */}
        <RoiSection />

        {/* Comparison Table */}
        <ComparisonTable 
          title={t('turnoverChecklist.comparison.title')}
          rows={[
            { feature: "Cost", manual: true, spreadsheet: true, rentalManager: true },
            { feature: "Cleaner reminders", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Photo proof", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Task tracking", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Multi-property", manual: false, spreadsheet: false, rentalManager: true },
            { feature: "Mobile-friendly", manual: false, spreadsheet: false, rentalManager: true }
          ]}
        />

        {/* Real-World Scenario */}
        <RealWorldScenarioSection />

        {/* AI/Smart Automation - Enhanced */}
        <section className="py-16 bg-gradient-to-r from-primary/5 to-secondary/10">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center mb-8">
              <Badge className="mb-4 animate-pulse hover:scale-110 transition-transform shadow-lg">{t('turnoverChecklist.aiSection.badge')}</Badge>
              <h2 className="text-3xl font-bold">{t('turnoverChecklist.aiSection.title')}</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <Card>
                  <CardContent className="pt-6">
                    <ul className="space-y-4">
                      {[
                        t('turnoverChecklist.aiSection.feature1'),
                        t('turnoverChecklist.aiSection.feature2'),
                        t('turnoverChecklist.aiSection.feature3'),
                        t('turnoverChecklist.aiSection.feature4')
                      ].map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Sparkles className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                          <p className="text-sm">{feature}</p>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
              
              <div className="flex items-center justify-center">
                <img 
                  src="/screenshots/properties.webp" 
                  alt="Mobile checklist view"
                  className="rounded-lg shadow-xl border border-border max-w-sm animate-[float_6s_ease-in-out_infinite]"
                />
              </div>
            </div>
            
            <p className="text-center text-sm text-muted-foreground">
              {t('turnoverChecklist.aiSection.footer')}
            </p>
          </div>
        </section>

        {/* Before/After Scenario */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">
              {t('turnoverChecklist.beforeAfter.title')}
            </h2>
            <BeforeAfterScenario
              beforeText={t('turnoverChecklist.beforeAfter.beforeText')}
              afterText={t('turnoverChecklist.beforeAfter.afterText')}
            />
          </div>
        </section>

        {/* Value Props */}
        <ValuePropsSection />

        {/* Common Mistakes */}
        <CommonMistakesSection />

        {/* FAQ */}
        <FaqSection faqs={(t('turnoverChecklist.seo.faq', { returnObjects: true }) || []) as Array<{ question: string; answer: string }>} />

        {/* Final CTA */}
        <FinalCTASection />

        {/* AI Assistant Summary */}
        <AiAssistantSummary />
      </LandingLayout>
    </>
  );
};

export default AirbnbTurnoverChecklist;
